/**
 * The scheduling block's side of the page.
 *
 * The block is a frame of a Reclaim scheduling link, on the page or in a
 * <dialog>; its markup, and why it is a frame the site writes and not
 * Reclaim's embed script, are in src/blocks/lazyblock-scheduling/block.php.
 * What that script did besides making the frame is done here, and one thing
 * it did not do: open the dialog.
 */

// What the scheduling page posts once a call is booked, and what it is told
// back. Reclaim's script knows the same two words.
const MEETING_BOOKED = 'MEETING_BOOKED';
const MEETING_BOOKED_COMPLETE = 'MEETING_BOOKED_COMPLETE';

// The origin a message from a frame has to carry: that of the address the
// block gave it, which in a dialog is still waiting in data-src.
const originOf = (frame) => {
  try {
    return new URL(frame.dataset.src || frame.getAttribute('src')).origin;
  } catch (e) {
    return null;
  }
};

const init = () => {
  const frames = [...document.querySelectorAll('iframe[data-scheduling]')]
    .map((frame) => ({ frame, origin: originOf(frame) }));

  if (!frames.length) return;

  window.addEventListener('message', (event) => {
    // Every frame and extension on a page can post to it. This one has to be
    // from a calendar's own window, and from Reclaim while it is in there.
    const from = frames.find(({ frame }) => frame.contentWindow === event.source);

    if (!from || from.origin !== event.origin) return;
    if (!event.data || event.data.type !== MEETING_BOOKED) return;

    // For Google Tag Manager, as contact_form_sent is for the form. No time
    // and no name: only that a call was booked, on the page it was booked on.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'meeting_booked' });

    // A scheduling link can name a page to send the visitor to afterwards, and
    // then its frame waits to hear back before it shows its own confirmation.
    // The visitor is sent nowhere -- data-redirect="NONE", in the embed code's
    // terms -- but the frame is still owed the answer.
    const { redirectUrl } = event.data.data || {};

    if (redirectUrl) {
      event.source.postMessage(MEETING_BOOKED_COMPLETE, from.origin);
    }
  });

  /* The calendar in a dialog
   *
   * Anything with data-dialog="<id>" opens the scheduling dialog of that id.
   * In practice that is a link, and it keeps its href: the address is where
   * the same offer is made without the dialog -- the contact form -- and is
   * what the link does when this cannot run, or is asked for another tab.
   */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog]');

    // A click with a key held is asking for the link, in a tab of its own.
    if (!opener || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const dialog = document.getElementById(opener.dataset.dialog);

    if (!dialog || !dialog.matches('dialog.makimo-blocks-scheduling') || !dialog.showModal) {
      return;
    }

    event.preventDefault();

    // The first time only. After that the frame is left as it is, so closing
    // the dialog half-way through a booking does not lose it.
    dialog.querySelectorAll('iframe[data-src]').forEach((frame) => {
      frame.src = frame.dataset.src;
      frame.removeAttribute('data-src');
    });

    dialog.showModal();
  });

  // A click on the dialog itself, and not on something in it, is one on the
  // backdrop: the panel covers all of the dialog's own box.
  document.querySelectorAll('dialog.makimo-blocks-scheduling').forEach((dialog) => {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  });
};

export default init;
